import type { ReactNode } from "react";
import { NavLink } from "react-router-dom";

/** The small pieces every screen is built from, drawn with `styles/ui.css`.
 * Each is presentational: it takes what to show and says what was chosen. */

/** A screen's title line: the name, a line saying what it is, and the one or
 * two actions that belong to the whole screen. */
export function PageHead({
  title,
  sub,
  actions,
}: {
  title: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-head">
      <h1>{title}</h1>
      {sub === undefined ? null : <p className="page-sub">{sub}</p>}
      {actions === undefined ? null : <div className="page-actions">{actions}</div>}
    </header>
  );
}

export interface SubTab {
  to: string;
  label: string;
  /** A count beside the label; `warn` draws it in amber. */
  count?: number | string;
  countTone?: "quiet" | "warn" | "crit";
  /** Match only this exact address, not the addresses below it. */
  end?: boolean;
}

/** The strip under the masthead that splits one section into its screens:
 * Sites | Exceptions | Red List, Planner | Plans | Schedule. Each is a link,
 * because each is its own address. */
export function SubTabs({
  tabs,
  end,
  inline = false,
  label,
}: {
  tabs: readonly SubTab[];
  /** Whatever sits at the far right of the strip: exports, a print button. */
  end?: ReactNode;
  inline?: boolean;
  label: string;
}) {
  return (
    <nav className={inline ? "subtabs inline" : "subtabs"} aria-label={label}>
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end ?? true}
          className={({ isActive }) => (isActive ? "subtab current" : "subtab")}
        >
          {tab.label}
          {tab.count === undefined ? null : (
            <span
              className={
                tab.countTone === "warn"
                  ? "count-badge warn"
                  : tab.countTone === "crit"
                    ? "count-badge crit"
                    : "count-badge"
              }
            >
              {tab.count}
            </span>
          )}
        </NavLink>
      ))}
      {end === undefined ? null : <div className="subtabs-end">{end}</div>}
    </nav>
  );
}

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
}

/** One of a few: All | Assess | Service | Replace. */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  fill = false,
}: {
  options: readonly SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  fill?: boolean;
}) {
  return (
    <div className={fill ? "segmented fill" : "segmented"} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** A toggle filter with an optional count: "Flagged 69". */
export function Pill({
  on,
  onToggle,
  children,
  count,
}: {
  on: boolean;
  onToggle: () => void;
  children: ReactNode;
  count?: number | string;
}) {
  return (
    <button type="button" className="pill" aria-pressed={on} onClick={onToggle}>
      {children}
      {count === undefined ? null : <span className="pill-count">{count}</span>}
    </button>
  );
}

export type Tone = "ok" | "warn" | "crit" | "info" | "draft" | "quiet";

/** A status as a coloured word. */
export function StatusChip({ tone = "quiet", children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`status-chip tone-${tone}`}>{children}</span>;
}

/** A number with a label. Given `onClick` it is a filter and says whether
 * it is on. */
export function Stat({
  label,
  value,
  tone,
  good = false,
  pressed,
  onClick,
}: {
  label: ReactNode;
  value: ReactNode;
  /** The colour of the top rule, as a CSS colour or custom property. */
  tone?: string;
  good?: boolean;
  pressed?: boolean;
  onClick?: () => void;
}) {
  const body = (
    <>
      <span className="stat-label">{label}</span>
      <span className={good ? "stat-value good" : "stat-value"}>{value}</span>
    </>
  );
  if (onClick === undefined) {
    return <div className="stat">{body}</div>;
  }
  return (
    <button
      type="button"
      className="stat"
      aria-pressed={pressed ?? false}
      onClick={onClick}
      style={tone === undefined ? undefined : ({ "--stat-tone": tone } as React.CSSProperties)}
    >
      {body}
    </button>
  );
}

/** A white card with an optional heading row. */
export function Card({
  title,
  sub,
  actions,
  children,
  className,
  flush = false,
}: {
  title?: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** No padding around the body: a table that runs to the card's edges. */
  flush?: boolean;
}) {
  return (
    <section className={className === undefined ? "card" : `card ${className}`}>
      {title === undefined && actions === undefined ? null : (
        <div className="card-head">
          {title === undefined ? null : <h2>{title}</h2>}
          {sub === undefined ? null : <span className="card-sub">{sub}</span>}
          {actions === undefined ? null : <div className="card-actions">{actions}</div>}
        </div>
      )}
      {flush ? children : <div className="card-body">{children}</div>}
    </section>
  );
}
